import React, { useState } from 'react';
import Card from './components/Card';
import Badge from './components/Badge';
import { 
  BookOpen, 
  Home, 
  FolderOpen, 
  BarChart3, 
  Search, 
  Gauge, 
  Sparkles, 
  AlertTriangle, 
  Settings, 
  User, 
  Key, 
  LayoutDashboard,
  Globe,
  TrendingUp,
  CheckCircle2,
  Info,
  Code,
  Download,
  RefreshCw,
  Eye,
  ChevronRight,
  ChevronDown,
  Zap,
  Target,
  List,
  FileCode,
  ExternalLink,
  Lightbulb,
  ArrowRight
} from 'lucide-react';

export default function Documentation() {
  const [expandedSections, setExpandedSections] = useState(new Set(['intro', 'dashboard']));

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  return (
    <div className="max-w-6xl mx-auto px-2 md:px-0 flex flex-col gap-8">
      {/* Header */}
      <Card>
        <div className="text-center py-6">
          <div className="inline-flex items-center justify-center p-3 bg-accent/10 rounded-xl mb-4">
            <BookOpen className="text-accent" size={32} />
          </div>
          <h1 className="font-heading text-3xl font-bold text-neutral-900 mb-2">
            Documentation
          </h1>
          <p className="text-blue-gray-600 max-w-2xl mx-auto">
            Guide complet pour comprendre et utiliser toutes les fonctionnalités de l'application
          </p>
        </div>
      </Card>

      {/* Navigation rapide */}
      <Card>
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-accent/10 rounded-lg">
            <List className="text-accent" size={24} />
          </div>
          <div>
            <div className="font-bold text-neutral-900 text-lg">Navigation rapide</div>
            <div className="text-neutral-500 text-sm">Accédez directement à une section</div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {[
            { id: 'intro', label: 'Introduction', icon: Info, desc: 'Découvrez l\'application' },
            { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, desc: 'Vue d\'ensemble' },
            { id: 'projects', label: 'Gestion des projets', icon: FolderOpen, desc: 'Créer et organiser' },
            { id: 'synthese', label: 'Synthèse', icon: LayoutDashboard, desc: 'Résumé du projet' },
            { id: 'seo', label: 'URL & SEO', icon: Search, desc: 'Analyse SEO détaillée' },
            { id: 'performance', label: 'Performance', icon: Gauge, desc: 'Métriques de vitesse' },
            { id: 'position', label: 'Positionnement IA', icon: Sparkles, desc: 'Recommandations IA' },
            { id: 'errors', label: 'Erreurs techniques', icon: AlertTriangle, desc: 'Problèmes détectés' },
            { id: 'settings', label: 'Paramètres', icon: Settings, desc: 'Configuration' },
            { id: 'profile', label: 'Profil', icon: User, desc: 'Compte et clés API' },
            { id: 'apis', label: 'Configuration APIs', icon: Key, desc: 'Guide Google APIs' }
          ].map(item => (
            <button
              key={item.id}
              onClick={() => {
                toggleSection(item.id);
                setTimeout(() => {
                  document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 100);
              }}
              className="group flex items-start gap-3 p-4 bg-neutral-50 rounded-lg border border-neutral-200/50 hover:border-accent/50 transition-all text-left"
            >
              <div className="p-2 bg-white rounded-lg border border-neutral-200/50 group-hover:bg-accent/10 group-hover:border-accent/50 transition-colors flex-shrink-0">
                <item.icon className="text-neutral-600 group-hover:text-accent transition-colors" size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-neutral-900 text-sm mb-1 group-hover:text-accent transition-colors">
                  {item.label}
                </div>
                <div className="text-xs text-neutral-500">
                  {item.desc}
                </div>
              </div>
              <ArrowRight 
                size={14} 
                className="text-neutral-400 group-hover:text-accent transition-colors flex-shrink-0 mt-0.5"
              />
            </button>
          ))}
        </div>
      </Card>

      {/* Introduction */}
      <Card id="intro">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-accent/10 rounded-lg">
            <Info className="text-accent" size={24} />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-bold text-neutral-900 text-lg">Introduction</div>
                <div className="text-neutral-500 text-sm">Découvrez ce que fait l'application</div>
              </div>
              <button
                onClick={() => toggleSection('intro')}
                className="p-1 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                {expandedSections.has('intro') ? (
                  <ChevronDown className="text-neutral-400" size={20} />
                ) : (
                  <ChevronRight className="text-neutral-400" size={20} />
                )}
              </button>
            </div>
          </div>
        </div>
        {expandedSections.has('intro') && (
          <div className="pt-4 border-t border-neutral-200 space-y-4">
            <p className="text-neutral-700 leading-relaxed">
              Cette application est une plateforme complète d'analyse SEO qui vous permet de :
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { icon: Search, text: 'Analyser vos sites web de manière approfondie (SEO, performance, accessibilité)' },
                { icon: TrendingUp, text: 'Suivre vos performances dans le temps avec des métriques détaillées' },
                { icon: Sparkles, text: 'Optimiser votre référencement grâce à l\'intelligence artificielle' },
                { icon: FileCode, text: 'Générer des fichiers SEO (llm.txt, Schema.org, Rich Snippets)' },
                { icon: AlertTriangle, text: 'Identifier les erreurs techniques et les problèmes de performance' }
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 bg-neutral-50 rounded-lg border border-neutral-200/50">
                  <item.icon className="text-accent mt-0.5 flex-shrink-0" size={18} />
                  <span className="text-sm text-neutral-700">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Dashboard */}
      <Card id="dashboard">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-accent/10 rounded-lg">
            <LayoutDashboard className="text-accent" size={24} />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-bold text-neutral-900 text-lg">Dashboard</div>
                <div className="text-neutral-500 text-sm">Vue d'ensemble de tous vos projets</div>
              </div>
              <div className="flex items-center gap-2">
                <Badge color="info">Page principale</Badge>
                <button
                  onClick={() => toggleSection('dashboard')}
                  className="p-1 hover:bg-neutral-100 rounded-lg transition-colors"
                >
                  {expandedSections.has('dashboard') ? (
                    <ChevronDown className="text-neutral-400" size={20} />
                  ) : (
                    <ChevronRight className="text-neutral-400" size={20} />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
        {expandedSections.has('dashboard') && (
          <div className="pt-4 border-t border-neutral-200 space-y-4">
            <div className="bg-blue-50/50 rounded-lg p-4 border border-blue-200/50">
              <div className="flex items-start gap-3">
                <Home className="text-blue-600 mt-0.5" size={18} />
                <div className="flex-1">
                  <div className="font-semibold text-neutral-900 mb-1 text-sm">Accès</div>
                  <p className="text-sm text-neutral-700">
                    Le Dashboard est la première page que vous voyez après connexion. 
                    C'est votre point d'entrée principal dans l'application.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <div className="font-semibold text-neutral-900 mb-3 flex items-center gap-2">
                <BarChart3 size={18} className="text-accent" />
                Fonctionnalités principales
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { icon: FolderOpen, title: 'Liste des projets', desc: 'Affiche tous vos projets avec leur statut, score SEO global et date du dernier audit.' },
                  { icon: Zap, title: 'Création rapide', desc: 'Bouton "Nouveau projet" pour démarrer rapidement une nouvelle analyse.' },
                  { icon: TrendingUp, title: 'Statistiques globales', desc: 'Vue d\'ensemble du nombre de projets et de leur état général.' },
                  { icon: Eye, title: 'Accès direct', desc: 'Cliquez sur une carte projet pour accéder directement à ses analyses.' }
                ].map((item, idx) => (
                  <div key={idx} className="p-4 bg-neutral-50 rounded-lg border border-neutral-200/50">
                    <div className="flex items-center gap-2 mb-2">
                      <item.icon size={16} className="text-accent" />
                      <div className="font-semibold text-sm text-neutral-900">{item.title}</div>
                    </div>
                    <p className="text-xs text-neutral-600">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Gestion des projets */}
      <Card id="projects">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-accent/10 rounded-lg">
            <FolderOpen className="text-accent" size={24} />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-bold text-neutral-900 text-lg">Gestion des projets</div>
                <div className="text-neutral-500 text-sm">Créer, modifier et organiser vos projets</div>
              </div>
              <button
                onClick={() => toggleSection('projects')}
                className="p-1 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                {expandedSections.has('projects') ? (
                  <ChevronDown className="text-neutral-400" size={20} />
                ) : (
                  <ChevronRight className="text-neutral-400" size={20} />
                )}
              </button>
            </div>
          </div>
        </div>
        {expandedSections.has('projects') && (
          <div className="pt-4 border-t border-neutral-200 space-y-4">
            <div>
              <div className="font-semibold text-neutral-900 mb-3 flex items-center gap-2">
                <FileCode size={18} className="text-accent" />
                Création d'un projet
              </div>
              <div className="space-y-3">
                {[
                  { step: '1', title: 'Saisie de l\'URL', desc: 'Entrez l\'URL de votre site web (ex: https://example.com). L\'application détectera automatiquement le sitemap.' },
                  { step: '2', title: 'Sélection des pages', desc: 'Choisissez les pages à analyser depuis le sitemap détecté. Vous pouvez sélectionner toutes les pages ou seulement certaines.' },
                  { step: '3', title: 'Lancement de l\'audit', desc: 'L\'application analyse automatiquement toutes les pages sélectionnées. Cette étape peut prendre quelques minutes selon le nombre de pages.' }
                ].map((item) => (
                  <div key={item.step} className="flex items-start gap-3 p-4 bg-neutral-50 rounded-lg border border-neutral-200/50">
                    <div className="w-6 h-6 rounded-full bg-accent text-white flex items-center justify-center text-xs font-semibold flex-shrink-0 mt-0.5">
                      {item.step}
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-sm text-neutral-900 mb-1">{item.title}</div>
                      <p className="text-xs text-neutral-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="font-semibold text-neutral-900 mb-3 flex items-center gap-2">
                <Settings size={18} className="text-accent" />
                Navigation dans un projet
              </div>
              <p className="text-sm text-neutral-700 mb-3">
                Une fois dans un projet, vous avez accès à plusieurs onglets via la sidebar :
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {[
                  { icon: LayoutDashboard, label: 'Synthèse', desc: "Vue d'ensemble du projet" },
                  { icon: Search, label: 'URL & SEO', desc: 'Analyse SEO détaillée' },
                  { icon: Gauge, label: 'Performance', desc: 'Métriques de vitesse' },
                  { icon: Sparkles, label: 'Positionnement IA', desc: 'Recommandations IA' },
                  { icon: AlertTriangle, label: 'Erreurs techniques', desc: 'Problèmes détectés' },
                  { icon: Settings, label: 'Paramètres', desc: 'Configuration du projet' }
                ].map(tab => (
                  <div key={tab.label} className="p-3 bg-neutral-50 rounded-lg border border-neutral-200/50">
                    <div className="flex items-center gap-2 mb-1">
                      <tab.icon size={16} className="text-accent" />
                      <div className="font-semibold text-sm text-neutral-900">{tab.label}</div>
                    </div>
                    <p className="text-xs text-neutral-600">{tab.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Synthèse */}
      <Card id="synthese">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-accent/10 rounded-lg">
            <LayoutDashboard className="text-accent" size={24} />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-bold text-neutral-900 text-lg">Synthèse</div>
                <div className="text-neutral-500 text-sm">Vue d'ensemble complète de votre projet</div>
              </div>
              <button
                onClick={() => toggleSection('synthese')}
                className="p-1 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                {expandedSections.has('synthese') ? (
                  <ChevronDown className="text-neutral-400" size={20} />
                ) : (
                  <ChevronRight className="text-neutral-400" size={20} />
                )}
              </button>
            </div>
          </div>
        </div>
        {expandedSections.has('synthese') && (
          <div className="pt-4 border-t border-neutral-200 space-y-4">
            <div className="bg-blue-50/50 rounded-lg p-4 border border-blue-200/50">
              <p className="text-sm text-neutral-700">
                La page <strong>Synthèse</strong> est l'onglet par défaut d'un projet. 
                Elle centralise toutes les informations importantes en un seul endroit.
              </p>
            </div>

            <div>
              <div className="font-semibold text-neutral-900 mb-3">Métriques clés</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { icon: TrendingUp, title: 'Score SEO moyen', desc: 'Score moyen calculé sur toutes les pages auditées (0-100)', color: 'success' },
                  { icon: Gauge, title: 'Score Performance moyen', desc: 'Score moyen de performance PageSpeed (0-100)', color: 'warning' },
                  { icon: Globe, title: 'Pages du sitemap', desc: 'Nombre total de pages détectées dans le sitemap', color: 'info' },
                  { icon: Target, title: 'Mots-clés identifiés', desc: 'Nombre de mots-clés extraits des audits', color: 'accent' }
                ].map((metric, idx) => (
                  <div key={idx} className="p-4 bg-neutral-50 rounded-lg border border-neutral-200/50">
                    <div className="flex items-center gap-2 mb-2">
                      <metric.icon size={16} className={`text-${metric.color === 'success' ? 'green' : metric.color === 'warning' ? 'orange' : metric.color === 'info' ? 'blue' : 'accent'}-600`} />
                      <div className="font-semibold text-sm text-neutral-900">{metric.title}</div>
                    </div>
                    <p className="text-xs text-neutral-600">{metric.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="font-semibold text-neutral-900 mb-3">Sections principales</div>
              <div className="space-y-2">
                {[
                  { title: 'Analyse de positionnement', desc: 'Points forts, points faibles et compréhension du secteur' },
                  { title: 'Liste de tâches', desc: 'Actions recommandées pour améliorer votre SEO' },
                  { title: 'Concurrents identifiés', desc: 'Sites web concurrents détectés par l\'IA' },
                  { title: 'Recommandations SEO', desc: 'Suggestions prioritaires pour optimiser votre référencement' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-neutral-50 rounded-lg border border-neutral-200/50">
                    <CheckCircle2 size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-semibold text-sm text-neutral-900">{item.title}</div>
                      <p className="text-xs text-neutral-600 mt-1">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* URL & SEO */}
      <Card id="seo">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-accent/10 rounded-lg">
            <Search className="text-accent" size={24} />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-bold text-neutral-900 text-lg">URL & SEO</div>
                <div className="text-neutral-500 text-sm">Analyse SEO détaillée page par page</div>
              </div>
              <button
                onClick={() => toggleSection('seo')}
                className="p-1 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                {expandedSections.has('seo') ? (
                  <ChevronDown className="text-neutral-400" size={20} />
                ) : (
                  <ChevronRight className="text-neutral-400" size={20} />
                )}
              </button>
            </div>
          </div>
        </div>
        {expandedSections.has('seo') && (
          <div className="pt-4 border-t border-neutral-200 space-y-4">
            <div className="bg-blue-50/50 rounded-lg p-4 border border-blue-200/50">
              <p className="text-sm text-neutral-700">
                Cette page affiche toutes les URLs de votre sitemap avec leur analyse SEO complète. 
                Vous pouvez analyser toutes les pages ou seulement certaines.
              </p>
            </div>

            <div>
              <div className="font-semibold text-neutral-900 mb-3">Fonctionnalités</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { icon: RefreshCw, title: 'Analyse en masse', desc: 'Bouton "Analyser toutes les pages" pour lancer l\'audit de toutes les URLs du sitemap.' },
                  { icon: Eye, title: 'Détail par page', desc: 'Cliquez sur une URL pour voir l\'analyse complète (titre, meta description, H1, etc.)' },
                  { icon: BarChart3, title: 'Tableau de données', desc: 'Score SEO, longueur du titre, présence H1, meta description, etc.' },
                  { icon: TrendingUp, title: 'Tri et filtrage', desc: 'Triez les pages par score, URL, ou autres critères pour identifier rapidement les problèmes.' }
                ].map((item, idx) => (
                  <div key={idx} className="p-4 bg-neutral-50 rounded-lg border border-neutral-200/50">
                    <div className="flex items-center gap-2 mb-2">
                      <item.icon size={16} className="text-accent" />
                      <div className="font-semibold text-sm text-neutral-900">{item.title}</div>
                    </div>
                    <p className="text-xs text-neutral-600">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-yellow-50/50 rounded-lg p-3 border border-yellow-200/50">
              <div className="flex items-start gap-2">
                <Info className="text-yellow-600 mt-0.5" size={16} />
                <div className="text-sm text-yellow-800">
                  <strong>Astuce :</strong> Les pages avec un score SEO faible sont prioritaires pour l'optimisation. 
                  Commencez par celles-ci pour améliorer rapidement votre référencement global.
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Performance */}
      <Card id="performance">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-accent/10 rounded-lg">
            <Gauge className="text-accent" size={24} />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-bold text-neutral-900 text-lg">Performance</div>
                <div className="text-neutral-500 text-sm">Analyse des métriques de vitesse et Core Web Vitals</div>
              </div>
              <button
                onClick={() => toggleSection('performance')}
                className="p-1 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                {expandedSections.has('performance') ? (
                  <ChevronDown className="text-neutral-400" size={20} />
                ) : (
                  <ChevronRight className="text-neutral-400" size={20} />
                )}
              </button>
            </div>
          </div>
        </div>
        {expandedSections.has('performance') && (
          <div className="pt-4 border-t border-neutral-200 space-y-4">
            <div className="bg-blue-50/50 rounded-lg p-4 border border-blue-200/50">
              <p className="text-sm text-neutral-700">
                Cette page utilise l'API Google PageSpeed Insights pour analyser les performances de vos pages. 
                Vous devez configurer une clé API PageSpeed dans votre profil pour utiliser cette fonctionnalité.
              </p>
            </div>

            <div>
              <div className="font-semibold text-neutral-900 mb-3">Métriques analysées</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { name: 'Score Performance Desktop', desc: 'Score de performance sur ordinateur (0-100)' },
                  { name: 'Score Performance Mobile', desc: 'Score de performance sur mobile (0-100)' },
                  { name: 'LCP (Largest Contentful Paint)', desc: 'Temps de chargement du contenu principal' },
                  { name: 'FID (First Input Delay)', desc: 'Délai avant la première interaction' },
                  { name: 'CLS (Cumulative Layout Shift)', desc: 'Stabilité visuelle de la page' },
                  { name: 'TBT (Total Blocking Time)', desc: 'Temps total de blocage du thread principal' }
                ].map((metric, idx) => (
                  <div key={idx} className="p-3 bg-neutral-50 rounded-lg border border-neutral-200/50">
                    <div className="font-semibold text-sm text-neutral-900 mb-1">{metric.name}</div>
                    <p className="text-xs text-neutral-600">{metric.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="font-semibold text-neutral-900 mb-3">Fonctionnalités</div>
              <div className="space-y-2">
                {[
                  { icon: RefreshCw, title: 'Audit en masse', desc: 'Analysez toutes les pages du sitemap en une fois' },
                  { icon: Download, title: 'Export rapport client', desc: 'JSON configurable depuis la synthèse : scores, carte d\'identité du site, GEO, pages de référence, tableau URLs… + commentaire consultant' },
                  { icon: Eye, title: 'Détail par page', desc: 'Consultez les recommandations détaillées pour chaque page' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-neutral-50 rounded-lg border border-neutral-200/50">
                    <item.icon size={16} className="text-accent mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-semibold text-sm text-neutral-900">{item.title}</div>
                      <p className="text-xs text-neutral-600 mt-1">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-orange-50/50 rounded-lg p-3 border border-orange-200/50">
              <div className="flex items-start gap-2">
                <AlertTriangle className="text-orange-600 mt-0.5" size={16} />
                <div className="text-sm text-orange-800">
                  <strong>Important :</strong> L'API PageSpeed Insights est limitée à 25 000 requêtes par mois en gratuit. 
                  Au-delà, un coût de 0,0001$ par requête s'applique.
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Positionnement IA */}
      <Card id="position">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-accent/10 rounded-lg">
            <Sparkles className="text-accent" size={24} />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-bold text-neutral-900 text-lg">Positionnement IA</div>
                <div className="text-neutral-500 text-sm">Analyse concurrentielle et recommandations SEO par IA</div>
              </div>
              <button
                onClick={() => toggleSection('position')}
                className="p-1 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                {expandedSections.has('position') ? (
                  <ChevronDown className="text-neutral-400" size={20} />
                ) : (
                  <ChevronRight className="text-neutral-400" size={20} />
                )}
              </button>
            </div>
          </div>
        </div>
        {expandedSections.has('position') && (
          <div className="pt-4 border-t border-neutral-200 space-y-4">
            <div className="bg-purple-50/50 rounded-lg p-4 border border-purple-200/50">
              <p className="text-sm text-neutral-700">
                Cette page utilise Google Gemini pour analyser votre positionnement concurrentiel et générer 
                des recommandations SEO personnalisées. Vous devez configurer une clé API Gemini dans votre profil.
              </p>
            </div>

            <div>
              <div className="font-semibold text-neutral-900 mb-3 flex items-center gap-2">
                <Target size={18} className="text-accent" />
                Analyse de positionnement
              </div>
              <div className="space-y-2">
                {[
                  { title: 'Secteur d\'activité', desc: 'Identification automatique du secteur' },
                  { title: 'Points forts', desc: 'Avantages concurrentiels détectés' },
                  { title: 'Points faibles', desc: 'Axes d\'amélioration identifiés' },
                  { title: 'Concurrents', desc: 'Sites web concurrents analysés' },
                  { title: 'Pages de référence', desc: 'Pages utilisées comme référence pour l\'analyse' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-neutral-50 rounded-lg border border-neutral-200/50">
                    <CheckCircle2 size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-semibold text-sm text-neutral-900">{item.title}</div>
                      <p className="text-xs text-neutral-600 mt-1">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="font-semibold text-neutral-900 mb-3 flex items-center gap-2">
                <Lightbulb size={18} className="text-accent" />
                Recommandations SEO
              </div>
              <p className="text-sm text-neutral-700 mb-3">
                L'IA génère des recommandations prioritaires basées sur l'analyse de votre site et de vos concurrents.
              </p>
              <div className="p-4 bg-neutral-50 rounded-lg border border-neutral-200/50">
                <ul className="space-y-2 text-sm text-neutral-700">
                  <li className="flex items-start gap-2">
                    <span className="text-accent font-semibold">•</span>
                    <span>Recommandations catégorisées (SEO technique, contenu, performance, etc.)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent font-semibold">•</span>
                    <span>Plan d'action priorisé avec tâches à réaliser</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent font-semibold">•</span>
                    <span>Régénération possible pour obtenir de nouvelles recommandations</span>
                  </li>
                </ul>
              </div>
            </div>

            <div>
              <div className="font-semibold text-neutral-900 mb-3 flex items-center gap-2">
                <Code size={18} className="text-accent" />
                Génération de fichiers
              </div>
              <p className="text-sm text-neutral-700 mb-3">
                Générez automatiquement des fichiers SEO optimisés pour votre site :
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  { name: 'llm.txt', desc: 'Fichier pour les LLM avec informations sur votre site' },
                  { name: 'Schema.org', desc: 'Données structurées pour l\'organisation' },
                  { name: 'Rich Snippets', desc: 'Données structurées pour les produits (si applicable)' }
                ].map((file, idx) => (
                  <div key={idx} className="p-3 bg-neutral-50 rounded-lg border border-neutral-200/50">
                    <div className="flex items-center gap-2 mb-2">
                      <Code size={16} className="text-accent" />
                      <div className="font-semibold text-sm text-neutral-900">{file.name}</div>
                    </div>
                    <p className="text-xs text-neutral-600">{file.desc}</p>
                  </div>
                ))}
              </div>
              <div className="mt-3 bg-blue-50/50 rounded-lg p-3 border border-blue-200/50">
                <p className="text-xs text-neutral-700">
                  <strong>Note :</strong> Les fichiers générés sont éditables avant sauvegarde ou téléchargement. 
                  Le fichier Rich Snippets n'apparaît que si votre site contient des produits.
                </p>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Erreurs techniques */}
      <Card id="errors">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-accent/10 rounded-lg">
            <AlertTriangle className="text-accent" size={24} />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-bold text-neutral-900 text-lg">Erreurs techniques</div>
                <div className="text-neutral-500 text-sm">Identification et résolution des problèmes</div>
              </div>
              <button
                onClick={() => toggleSection('errors')}
                className="p-1 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                {expandedSections.has('errors') ? (
                  <ChevronDown className="text-neutral-400" size={20} />
                ) : (
                  <ChevronRight className="text-neutral-400" size={20} />
                )}
              </button>
            </div>
          </div>
        </div>
        {expandedSections.has('errors') && (
          <div className="pt-4 border-t border-neutral-200 space-y-4">
            <div className="bg-red-50/50 rounded-lg p-4 border border-red-200/50">
              <p className="text-sm text-neutral-700">
                Cette page liste tous les problèmes techniques détectés sur votre site, 
                classés par sévérité et type.
              </p>
            </div>

            <div>
              <div className="font-semibold text-neutral-900 mb-3">Types d'erreurs</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { type: 'SEO', color: 'text-blue-600', desc: 'Problèmes de référencement (balises manquantes, etc.)' },
                  { type: 'Performance', color: 'text-orange-600', desc: 'Problèmes de vitesse et optimisation' },
                  { type: 'Accessibilité', color: 'text-purple-600', desc: 'Problèmes d\'accessibilité WCAG' },
                  { type: 'Technique', color: 'text-red-600', desc: 'Erreurs techniques (404, 500, etc.)' }
                ].map((error, idx) => (
                  <div key={idx} className="p-3 bg-neutral-50 rounded-lg border border-neutral-200/50">
                    <div className="flex items-center gap-2 mb-1">
                      <AlertTriangle size={16} className={error.color} />
                      <div className="font-semibold text-sm text-neutral-900">{error.type}</div>
                    </div>
                    <p className="text-xs text-neutral-600">{error.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="font-semibold text-neutral-900 mb-3">Niveaux de sévérité</div>
              <div className="space-y-2">
                {[
                  { level: 'Haute', color: 'error', desc: 'Problèmes critiques nécessitant une action immédiate' },
                  { level: 'Moyenne', color: 'warning', desc: 'Problèmes importants à corriger rapidement' },
                  { level: 'Basse', color: 'info', desc: 'Améliorations recommandées' }
                ].map((severity, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-neutral-50 rounded-lg border border-neutral-200/50">
                    <Badge color={severity.color} className="flex-shrink-0">{severity.level}</Badge>
                    <p className="text-xs text-neutral-600 flex-1">{severity.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-blue-50/50 rounded-lg p-3 border border-blue-200/50">
              <div className="flex items-start gap-2">
                <Info className="text-blue-600 mt-0.5" size={16} />
                <div className="text-sm text-neutral-700">
                  <strong>Astuce :</strong> Triez les erreurs par sévérité pour prioriser les corrections. 
                  Les erreurs de haute sévérité impactent directement votre référencement et vos performances.
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Paramètres */}
      <Card id="settings">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-accent/10 rounded-lg">
            <Settings className="text-accent" size={24} />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-bold text-neutral-900 text-lg">Paramètres du projet</div>
                <div className="text-neutral-500 text-sm">Configuration et modification des informations</div>
              </div>
              <button
                onClick={() => toggleSection('settings')}
                className="p-1 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                {expandedSections.has('settings') ? (
                  <ChevronDown className="text-neutral-400" size={20} />
                ) : (
                  <ChevronRight className="text-neutral-400" size={20} />
                )}
              </button>
            </div>
          </div>
        </div>
        {expandedSections.has('settings') && (
          <div className="pt-4 border-t border-neutral-200 space-y-4">
            <div>
              <div className="font-semibold text-neutral-900 mb-3">Informations modifiables</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { field: 'Nom du projet', desc: 'Nom d\'affichage du projet' },
                  { field: 'URL du site', desc: 'URL principale du site web' },
                  { field: 'Favicon', desc: 'Icône du projet (téléchargement possible)' },
                  { field: 'Description', desc: 'Description optionnelle du projet' }
                ].map((item, idx) => (
                  <div key={idx} className="p-3 bg-neutral-50 rounded-lg border border-neutral-200/50">
                    <div className="font-semibold text-sm text-neutral-900 mb-1">{item.field}</div>
                    <p className="text-xs text-neutral-600">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-yellow-50/50 rounded-lg p-3 border border-yellow-200/50">
              <div className="flex items-start gap-2">
                <AlertTriangle className="text-yellow-600 mt-0.5" size={16} />
                <div className="text-sm text-yellow-800">
                  <strong>Attention :</strong> La modification de l'URL du site peut nécessiter une nouvelle analyse complète 
                  si le sitemap change.
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Profil */}
      <Card id="profile">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-accent/10 rounded-lg">
            <User className="text-accent" size={24} />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-bold text-neutral-900 text-lg">Profil utilisateur</div>
                <div className="text-neutral-500 text-sm">Gestion de votre compte et clés API</div>
              </div>
              <button
                onClick={() => toggleSection('profile')}
                className="p-1 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                {expandedSections.has('profile') ? (
                  <ChevronDown className="text-neutral-400" size={20} />
                ) : (
                  <ChevronRight className="text-neutral-400" size={20} />
                )}
              </button>
            </div>
          </div>
        </div>
        {expandedSections.has('profile') && (
          <div className="pt-4 border-t border-neutral-200 space-y-4">
            <div>
              <div className="font-semibold text-neutral-900 mb-3 flex items-center gap-2">
                <User size={18} className="text-accent" />
                Informations personnelles
              </div>
              <div className="p-4 bg-neutral-50 rounded-lg border border-neutral-200/50">
                <ul className="space-y-2 text-sm text-neutral-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Modification du nom et prénom</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Changement de l'adresse email</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Modification du mot de passe</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Upload d'un avatar personnalisé</span>
                  </li>
                </ul>
              </div>
            </div>

            <div>
              <div className="font-semibold text-neutral-900 mb-3 flex items-center gap-2">
                <Key size={18} className="text-accent" />
                Clés API
              </div>
              <div className="space-y-3">
                <div className="p-4 bg-neutral-50 rounded-lg border border-neutral-200/50">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles size={16} className="text-purple-600" />
                    <div className="font-semibold text-sm text-neutral-900">Clé API Gemini</div>
                  </div>
                  <p className="text-xs text-neutral-600 mb-2">
                    Nécessaire pour l'analyse de positionnement IA et la génération de recommandations.
                  </p>
                  <Badge color="info" className="text-xs">Obligatoire pour Positionnement IA</Badge>
                </div>
                <div className="p-4 bg-neutral-50 rounded-lg border border-neutral-200/50">
                  <div className="flex items-center gap-2 mb-2">
                    <Gauge size={16} className="text-orange-600" />
                    <div className="font-semibold text-sm text-neutral-900">Clé API PageSpeed</div>
                  </div>
                  <p className="text-xs text-neutral-600 mb-2">
                    Nécessaire pour l'analyse des performances et Core Web Vitals.
                  </p>
                  <Badge color="warning" className="text-xs">Obligatoire pour Performance</Badge>
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Configuration APIs */}
      <Card id="apis">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-accent/10 rounded-lg">
            <Key className="text-accent" size={24} />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-bold text-neutral-900 text-lg">Configuration des APIs Google</div>
                <div className="text-neutral-500 text-sm">Guide pour configurer Google Analytics, Search Console et PageSpeed</div>
              </div>
              <button
                onClick={() => toggleSection('apis')}
                className="p-1 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                {expandedSections.has('apis') ? (
                  <ChevronDown className="text-neutral-400" size={20} />
                ) : (
                  <ChevronRight className="text-neutral-400" size={20} />
                )}
              </button>
            </div>
          </div>
        </div>
        {expandedSections.has('apis') && (
          <div className="pt-4 border-t border-neutral-200 space-y-4">
            <div className="bg-blue-50/50 rounded-lg p-4 border border-blue-200/50">
              <div className="flex items-start gap-3">
                <Info className="text-blue-600 mt-0.5" size={20} />
                <div className="flex-1">
                  <div className="font-semibold text-neutral-900 mb-2 text-sm">Configuration OAuth obligatoire</div>
                  <p className="text-sm text-neutral-700 mb-3">
                    Pour utiliser les APIs Google, vous devez configurer l'écran de consentement OAuth dans Google Cloud Console.
                  </p>
                  <div className="bg-white rounded-lg p-3 border border-blue-200">
                    <div className="font-medium text-neutral-900 mb-2 text-sm">Différence entre clés API et OAuth2 :</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                      <div>
                        <div className="font-semibold text-neutral-900 mb-1">Clés API (Search Console, PageSpeed)</div>
                        <ul className="space-y-1 text-neutral-600">
                          <li>• Format : AIza...</li>
                          <li>• Accès public aux données</li>
                          <li>• Configuration simple</li>
                        </ul>
                      </div>
                      <div>
                        <div className="font-semibold text-neutral-900 mb-1">OAuth2 (Google Analytics)</div>
                        <ul className="space-y-1 text-neutral-600">
                          <li>• Format : 123456789-abcdef.apps.googleusercontent.com</li>
                          <li>• Accès aux données utilisateur</li>
                          <li>• Nécessite consentement</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {[
                { 
                  icon: BarChart3, 
                  title: 'Google Analytics', 
                  color: 'blue',
                  steps: [
                    'Créez un projet Google Cloud',
                    'Activez l\'API Google Analytics Data API',
                    'Créez un ID client OAuth 2.0',
                    'Configurez l\'écran de consentement OAuth',
                    'Ajoutez le scope : https://www.googleapis.com/auth/analytics.readonly'
                  ]
                },
                { 
                  icon: Search, 
                  title: 'Google Search Console', 
                  color: 'green',
                  steps: [
                    'Créez un projet Google Cloud',
                    'Activez l\'API Search Console',
                    'Créez une clé API',
                    'Restreignez la clé à "Search Console API"',
                    'Configurez votre propriété dans Search Console'
                  ]
                },
                { 
                  icon: Gauge, 
                  title: 'Google PageSpeed Insights', 
                  color: 'orange',
                  steps: [
                    'Créez un projet Google Cloud',
                    'Activez l\'API PageSpeed Insights',
                    'Créez une clé API',
                    'Restreignez la clé à "PageSpeed Insights API"'
                  ],
                  warning: 'Limite : 25 000 requêtes/mois gratuites, puis 0,0001$ par requête.'
                }
              ].map((api, idx) => (
                <div key={idx} className="p-4 bg-neutral-50 rounded-lg border border-neutral-200/50">
                  <div className="flex items-center gap-2 mb-3">
                    <api.icon size={18} className={`text-${api.color}-600`} />
                    <div className="font-semibold text-neutral-900">{api.title}</div>
                  </div>
                  <ol className="list-decimal list-inside space-y-1 text-sm text-neutral-700 ml-2">
                    {api.steps.map((step, stepIdx) => (
                      <li key={stepIdx}>{step}</li>
                    ))}
                  </ol>
                  {api.warning && (
                    <div className="mt-3 bg-orange-50 rounded p-2 border border-orange-200">
                      <p className="text-xs text-orange-800">
                        <strong>Limite :</strong> {api.warning}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="bg-green-50/50 rounded-lg p-4 border border-green-200/50">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="text-green-600 mt-0.5" size={16} />
                <div className="text-sm text-green-800">
                  <strong>Liens utiles :</strong>
                  <ul className="mt-2 space-y-1">
                    <li>• <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-green-900">Google Cloud Console</a></li>
                    <li>• <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-green-900">Google Analytics</a></li>
                    <li>• <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer" className="underline hover:text-green-900">Search Console</a></li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
